import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, ChevronLeft, ChevronRight, Play, Pause } from "lucide-react";
import { heroSlides } from "../data/mock";

export default function VideoHero() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);
  const videoRefs = useRef([]);
  const SLIDE_MS = 7500;

  useEffect(() => {
    if (paused) return;
    timerRef.current = setTimeout(() => {
      setActive((p) => (p + 1) % heroSlides.length);
    }, SLIDE_MS);
    return () => clearTimeout(timerRef.current);
  }, [active, paused]);

  useEffect(() => {
    videoRefs.current.forEach((v, i) => {
      if (!v) return;
      if (i === active) {
        v.currentTime = 0;
        v.play().catch(() => {});
      } else {
        try { v.pause(); } catch {}
      }
    });
  }, [active]);

  const go = (dir) => {
    setActive((p) => (p + dir + heroSlides.length) % heroSlides.length);
  };

  return (
    <section id="home" className="relative w-full min-h-[100svh] overflow-hidden grain">
      {/* Slides */}
      {heroSlides.map((s, i) => (
        <div
          key={s.id}
          className={`absolute inset-0 transition-opacity duration-[1100ms] ${
            i === active ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={s.poster}
            alt=""
            className={`absolute inset-0 w-full h-full object-cover ${i === active ? "kenburns" : ""}`}
          />
          {s.video ? (
            <video
              ref={(el) => (videoRefs.current[i] = el)}
              className="absolute inset-0 w-full h-full object-cover"
              src={s.video}
              poster={s.poster}
              muted
              loop
              playsInline
              preload="auto"
            />
          ) : null}
          {/* Overlays */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/35 to-black/90" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/20 to-transparent" />
        </div>
      ))}

      {/* Side rail */}
      <div className="absolute left-5 lg:left-10 top-1/2 -translate-y-1/2 hidden md:flex flex-col items-center gap-3 z-20">
        <div className="w-px h-16 bg-white/30" />
        <div className="text-[10px] tracking-[0.35em] text-white/70 rotate-180 [writing-mode:vertical-rl]">SINCE 1957</div>
        <div className="w-px h-16 bg-white/30" />
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-[100svh] max-w-[1400px] mx-auto px-6 lg:px-10 flex items-end pt-36 lg:pt-44 pb-28 lg:pb-32">
        <div className="max-w-3xl">
          <div
            key={`eyebrow-${active}`}
            className="inline-flex items-center gap-3 px-3 py-1.5 rounded-full border border-white/15 bg-white/5 backdrop-blur text-[11px] uppercase tracking-[0.25em] text-white/85 mb-7 animate-[fadeUp_0.8s_ease]"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#F5D000] animate-pulse" />
            {heroSlides[active].eyebrow}
          </div>

          <h1 key={`title-${active}`} className="font-display text-white text-[clamp(34px,7vh,40px)] leading-[1.05] sm:text-[clamp(38px,8vh,52px)] md:text-[clamp(42px,8.5vh,64px)] lg:text-[clamp(46px,9vh,80px)] font-bold mb-5">
            <span className="block animate-[fadeUp_0.9s_ease]">{heroSlides[active].title}</span>
            <span className="block text-[#F5D000] animate-[fadeUp_1.05s_ease]">
              {heroSlides[active].titleAccent}
            </span>
          </h1>

          <p key={`sub-${active}`} className="text-white/80 max-w-xl text-base sm:text-lg leading-relaxed mb-9 animate-[fadeUp_1.2s_ease]">
            {heroSlides[active].subtitle}
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <a href="#contact" className="btn-amber px-7 py-3.5 rounded-md font-semibold text-sm flex items-center gap-2">
              Get in Touch <ArrowRight className="w-4 h-4" />
            </a>
            <a href="#projects" className="btn-ghost px-7 py-3.5 rounded-md font-semibold text-sm backdrop-blur-md">
              View Our Work
            </a>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="absolute right-6 lg:right-10 bottom-24 lg:bottom-28 z-20 flex items-center gap-3">
        <button
          aria-label="Previous"
          onClick={() => go(-1)}
          className="w-11 h-11 rounded-full border border-white/20 bg-black/30 backdrop-blur text-white/90 hover:bg-[#F5D000] hover:text-black hover:border-[#F5D000] transition-all flex items-center justify-center"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          aria-label={paused ? "Play" : "Pause"}
          onClick={() => setPaused((p) => !p)}
          className="w-11 h-11 rounded-full border border-white/20 bg-black/30 backdrop-blur text-white/90 hover:bg-white hover:text-black transition-all flex items-center justify-center"
        >
          {paused ? <Play className="w-4 h-4 ml-0.5" /> : <Pause className="w-4 h-4" />}
        </button>
        <button
          aria-label="Next"
          onClick={() => go(1)}
          className="w-11 h-11 rounded-full border border-white/20 bg-black/30 backdrop-blur text-white/90 hover:bg-[#F5D000] hover:text-black hover:border-[#F5D000] transition-all flex items-center justify-center"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Progress / indicators */}
      <div className="absolute bottom-10 left-0 right-0 z-20 px-6 lg:px-10">
        <div className="max-w-[1400px] mx-auto flex items-end justify-between gap-6">
          <div className="flex items-center gap-3">
            {heroSlides.map((_, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                className="group flex items-center gap-2"
                aria-label={`Go to slide ${i + 1}`}
              >
                <span className={`text-[11px] tabular-nums tracking-wider ${i === active ? "text-white" : "text-white/45"}`}>
                  0{i + 1}
                </span>
                <span className="relative h-px w-14 bg-white/20 overflow-hidden">
                  <span
                    className={`absolute inset-y-0 left-0 bg-[#F5D000] transition-[width] ease-linear ${
                      i === active ? (paused ? "" : "animate-[grow_7.5s_linear]") : ""
                    }`}
                    style={{ width: i === active ? "100%" : i < active ? "100%" : "0%" }}
                  />
                </span>
              </button>
            ))}
          </div>
          <div className="hidden sm:block text-[11px] uppercase tracking-[0.3em] text-white/55">Scroll ↓</div>
        </div>
      </div>

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(28px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes grow {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
    </section>
  );
}
